import React, { useState, useEffect } from 'react';
import api from '../../api';
import { Download, Upload, Users, AlertTriangle, CheckCircle, FileText, FileDown, Search, Trash2, Pencil } from 'lucide-react';
import UIElementGuard from '../UIElementGuard';
import ConfirmModal from '../ConfirmModal';

const FacultyManagement = ({ isDeactivatedView = false }) => {
  const [faculty, setFaculty] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [permissionError, setPermissionError] = useState('');
  
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingFaculty, setEditingFaculty] = useState(null);
  const [editForm, setEditForm] = useState({ full_name: '', faculty_id: '', department: '', designation: '', phone_number: '' });

  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'danger',
    onConfirm: null
  });
  const closeConfirmModal = () => setConfirmModal(prev => ({ ...prev, isOpen: false }));

  useEffect(() => {
    fetchFaculty();
  }, [isDeactivatedView]);

  const handleToggleStatus = async (facultyMember) => {
    const newActive = !facultyMember.is_active;
    const newStatus = newActive ? 'Active' : 'Deactivated';
    const actionText = newActive ? 'activate' : 'deactivate';
    
    setConfirmModal({
      isOpen: true,
      title: `Confirm ${actionText.charAt(0).toUpperCase() + actionText.slice(1)}`,
      message: `Are you sure you want to ${actionText} ${facultyMember.full_name || 'this faculty member'}?`,
      type: newActive ? 'primary' : 'danger',
      onConfirm: async () => {
        closeConfirmModal();
        try {
          await api.put(`/university/users/${facultyMember.id}/status`, {
            is_active: newActive,
            status: newStatus
          });
          fetchFaculty();
        } catch (err) {
          console.error("Failed to update faculty status", err);
          alert(err.response?.data?.detail || `Failed to ${actionText} faculty member`);
        }
      }
    });
  };

  const handleDeleteFaculty = async (facultyId) => {
    setConfirmModal({
      isOpen: true,
      title: 'Delete Faculty Member',
      message: "Are you sure you want to delete this faculty member? This will permanently delete their account.",
      type: 'danger',
      onConfirm: async () => {
        closeConfirmModal();
        try {
          await api.delete(`/faculty/${facultyId}`);
          fetchFaculty();
        } catch (err) {
          console.error("Failed to delete faculty member", err);
          alert(err.response?.data?.detail || "Failed to delete faculty member");
        }
      }
    });
  };

  const handleOpenEdit = (facultyMember) => {
    setEditingFaculty(facultyMember);
    setEditForm({
      full_name: facultyMember.full_name || '',
      faculty_id: facultyMember.faculty_id || '',
      department: facultyMember.department || '',
      designation: facultyMember.designation || '',
      phone_number: facultyMember.phone || ''
    });
    setEditModalOpen(true);
  };

  const handleUpdateFaculty = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/faculty/${editingFaculty.id}`, editForm);
      setEditModalOpen(false);
      fetchFaculty();
    } catch (err) {
      console.error("Failed to update faculty", err);
      alert(err.response?.data?.detail || "Failed to update faculty");
    }
  };

  const fetchFaculty = async () => {
    setLoading(true);
    setPermissionError('');
    try {
      const res = await api.get(`/faculty?status=${isDeactivatedView ? 'deactivated' : 'active'}`);
      setFaculty(res.data || []);
    } catch (err) {
      console.error("Failed to fetch faculty", err);
      if (err.response && err.response.status === 403) {
        setPermissionError(err.response?.data?.detail || "You don't have permission to access this resource.");
      }
    } finally {
      setLoading(false);
    }
  };

  const downloadTemplate = async () => {
    try {
      const response = await api.get('/faculty/template', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'faculty_upload_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Error downloading template", error);
      alert("Failed to download template");
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploadFile(file);
      previewUpload(file);
    }
  };

  const previewUpload = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    try {
      setUploading(true);
      const res = await api.post('/faculty/upload-preview', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setPreviewData(res.data);
    } catch (error) {
      console.error("Preview failed", error);
      alert("Failed to process file preview.");
      setUploadFile(null);
    } finally {
      setUploading(false);
    }
  };

  const commitUpload = async () => {
    if (!uploadFile) return;
    const formData = new FormData();
    formData.append('file', uploadFile);
    try {
      setUploading(true);
      const res = await api.post('/faculty/upload-commit', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setUploadResult(res.data);
      setPreviewData(null);
      fetchFaculty();
    } catch (error) {
      console.error("Upload failed", error);
      alert("Failed to upload faculty.");
    } finally {
      setUploading(false);
    }
  };

  const closeUploadModal = () => {
    setUploadModalOpen(false);
    setUploadFile(null);
    setPreviewData(null);
    setUploadResult(null);
  };

  const filteredFaculty = faculty.filter(f => {
    const q = searchQuery.toLowerCase();
    if (!q) return true;
    return (
      (f.full_name && String(f.full_name).toLowerCase().includes(q)) ||
      (f.email && String(f.email).toLowerCase().includes(q)) ||
      (f.department && String(f.department).toLowerCase().includes(q)) ||
      (f.faculty_id && String(f.faculty_id).toLowerCase().includes(q)) ||
      (f.designation && String(f.designation).toLowerCase().includes(q))
    );
  });

  return (
    <div style={{ padding: '20px 0' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px', flexWrap: 'wrap', gap: '15px' }}>
        <div>
          <h2 style={{ fontSize: '1.5rem', fontWeight: '700', color: 'var(--text-main, #1e293b)', margin: '0 0 4px 0' }}>
            {isDeactivatedView ? 'Deactivated Faculty' : 'Faculty Management'}
          </h2>
          <p style={{ color: 'var(--text-muted, #64748b)', margin: 0, fontSize: '0.95rem' }}>
            {isDeactivatedView ? 'View and reactivate deactivated faculty accounts.' : 'Manage faculty members and their access to the platform.'}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          {!isDeactivatedView && (
            <UIElementGuard page="faculty_directory" element="add_faculty_btn">
              <button
                onClick={() => setUploadModalOpen(true)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '10px 18px',
                  borderRadius: '8px',
                  background: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  fontWeight: '600',
                  fontSize: '0.9rem',
                  cursor: 'pointer',
                  boxShadow: '0 4px 12px rgba(59, 130, 246, 0.25)'
                }}
              >
                <Upload size={16} /> Add Faculty
              </button>
            </UIElementGuard>
          )}
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', gap: '15px', flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: '1', minWidth: '250px', maxWidth: '400px' }}>
          <Search size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
          <input
            type="text"
            placeholder="Search faculty by name, email, or department..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              width: '100%',
              padding: '10px 12px 10px 38px',
              borderRadius: '8px',
              border: '1px solid #cbd5e1',
              background: 'var(--card-bg, #ffffff)',
              color: 'var(--text-main, #1e293b)',
              fontSize: '0.9rem'
            }}
          />
        </div>
        <div style={{ fontSize: '0.9rem', color: '#64748b', fontWeight: '500' }}>
          {filteredFaculty.length} {filteredFaculty.length === 1 ? 'Member' : 'Members'}
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <div className="spinner" />
          <p style={{ color: '#64748b', marginTop: '10px' }}>Loading Faculty Members...</p>
        </div>
      ) : permissionError ? (
        <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '12px', padding: '20px', textAlign: 'center', color: '#991b1b' }}>
          <h4 style={{ margin: '0 0 8px 0', fontSize: '1rem', fontWeight: '600' }}>Access Restricted</h4>
          <p style={{ margin: 0, fontSize: '0.9rem' }}>{permissionError}</p>
        </div>
      ) : filteredFaculty.length === 0 ? (
        <div style={{ background: 'var(--card-bg, #ffffff)', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '40px', textAlign: 'center' }}>
          <Users size={40} style={{ color: '#94a3b8', marginBottom: '12px' }} />
          <h3 style={{ margin: '0 0 6px 0', fontSize: '1.1rem', color: '#1e293b' }}>
            {isDeactivatedView ? 'No Deactivated Faculty Accounts' : 'No Faculty Members Found'}
          </h3>
          <p style={{ margin: '0 0 16px 0', color: '#64748b', fontSize: '0.9rem' }}>
            {isDeactivatedView ? 'All faculty accounts are currently active.' : 'Add your faculty members to manage their access and assignments.'}
          </p>
        </div>
      ) : (
        <div style={{ background: 'var(--card-bg, #ffffff)', border: '1px solid #e2e8f0', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '0.9rem' }}>
            <thead>
              <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                <th style={{ padding: '12px 16px', color: '#475569', fontWeight: '600' }}>Name</th>
                <th style={{ padding: '12px 16px', color: '#475569', fontWeight: '600' }}>Email</th>
                <th style={{ padding: '12px 16px', color: '#475569', fontWeight: '600' }}>Faculty ID</th>
                <th style={{ padding: '12px 16px', color: '#475569', fontWeight: '600' }}>Department</th>
                <th style={{ padding: '12px 16px', color: '#475569', fontWeight: '600' }}>Designation</th>
                <th style={{ padding: '12px 16px', color: '#475569', fontWeight: '600' }}>Status</th>
                <th style={{ padding: '12px 16px', color: '#475569', fontWeight: '600', textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredFaculty.map(f => (
                <tr key={f.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '14px 16px', fontWeight: '600', color: '#0f172a' }}>{f.full_name || 'N/A'}</td>
                  <td style={{ padding: '14px 16px', color: '#3b82f6' }}>{f.email}</td>
                  <td style={{ padding: '14px 16px', color: '#64748b' }}>{f.faculty_id || 'N/A'}</td>
                  <td style={{ padding: '14px 16px', color: '#64748b' }}>{f.department || 'N/A'}</td>
                  <td style={{ padding: '14px 16px', color: '#64748b' }}>{f.designation || 'N/A'}</td>
                  <td style={{ padding: '14px 16px' }}>
                    <span style={{
                      padding: '4px 10px',
                      borderRadius: '20px',
                      fontSize: '0.78rem',
                      fontWeight: '700',
                      background: f.is_active ? '#dcfce7' : '#fee2e2',
                      color: f.is_active ? '#166534' : '#991b1b',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '5px'
                    }}>
                      <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: f.is_active ? '#22c55e' : '#ef4444' }}></span>
                      {f.is_active ? 'Active' : 'Deactivated'}
                    </span>
                  </td>
                  <td style={{ padding: '14px 16px', textAlign: 'right', display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                    <button
                      onClick={() => handleToggleStatus(f)}
                      style={{
                        background: f.is_active ? '#fff1f2' : '#f0fdf4',
                        color: f.is_active ? '#e11d48' : '#16a34a',
                        border: `1px solid ${f.is_active ? '#fda4af' : '#86efac'}`,
                        cursor: 'pointer',
                        padding: '6px 12px',
                        borderRadius: '6px',
                        fontSize: '0.8rem',
                        fontWeight: '700',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}
                      title={f.is_active ? "Deactivate Account" : "Activate Account"}
                    >
                      {f.is_active ? 'Deactivate' : 'Activate'}
                    </button>
                    {!isDeactivatedView && (
                      <UIElementGuard page="faculty_directory" element="edit_faculty_btn">
                        <button
                          onClick={() => handleOpenEdit(f)}
                          style={{
                            background: '#eff6ff',
                            color: '#3b82f6',
                            border: '1px solid #bfdbfe',
                            cursor: 'pointer',
                            padding: '6px',
                            borderRadius: '6px',
                            display: 'inline-flex',
                            alignItems: 'center'
                          }}
                          title="Edit Faculty Member"
                        >
                          <Pencil size={17} />
                        </button>
                      </UIElementGuard>
                    )}
                    {!isDeactivatedView && (
                      <UIElementGuard page="faculty_directory" element="delete_faculty_btn">
                        <button
                          onClick={() => handleDeleteFaculty(f.id)}
                          style={{
                            background: '#fff1f2',
                            color: '#e11d48',
                            border: '1px solid #fda4af',
                            cursor: 'pointer',
                            padding: '6px',
                            borderRadius: '6px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            marginLeft: '4px'
                          }}
                          title="Delete Faculty Member"
                        >
                          <Trash2 size={17} />
                        </button>
                      </UIElementGuard>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Upload Modal */}
      {uploadModalOpen && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', padding: '30px', borderRadius: '12px', width: '90%', maxWidth: '700px', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, fontSize: '1.25rem' }}>Bulk Upload Faculty</h3>
              <button onClick={closeUploadModal} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#94a3b8' }}>&times;</button>
            </div>
            {!uploadFile && !uploadResult && (
              <div>
                <div style={{ padding: '20px', background: '#f8fafc', border: '1px dashed #cbd5e1', borderRadius: '8px' }}>
                  <h4 style={{ margin: '0 0 10px 0', color: '#334155' }}>Upload Data</h4>
                  <p style={{ margin: '0 0 15px 0', color: '#475569', fontSize: '0.9rem' }}>Upload the completed Excel file to import faculty accounts.</p>
                  <label
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '10px 18px',
                      borderRadius: '8px',
                      background: '#3b82f6',
                      color: 'white',
                      border: 'none',
                      fontWeight: '600',
                      fontSize: '0.9rem',
                      cursor: 'pointer',
                      boxShadow: '0 4px 12px rgba(59, 130, 246, 0.25)',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <Upload size={16} /> Select Excel File
                    <input type="file" accept=".xlsx,.xls" style={{ display: 'none' }} onChange={handleFileChange} disabled={uploading} />
                  </label>
                  
                  <div style={{ marginTop: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ color: '#64748b', fontSize: '13px' }}>Need the correct format?</span>
                    <button
                      onClick={downloadTemplate}
                      style={{
                        background: 'none', border: 'none', color: '#3b82f6', cursor: 'pointer', fontSize: '13px', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '4px', padding: 0
                      }}
                    >
                      <FileDown size={14} /> Download Template
                    </button>
                  </div>
                </div>
              </div>
            )}

            {uploading && <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>Processing file...</div>}

            {previewData && !uploading && (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '15px', color: '#0369a1', fontWeight: '500' }}>
                  <FileText size={20} /> Found {previewData.total_rows} records in file
                  {previewData.invalid_rows > 0 && (
                    <span style={{ color: '#dc2626' }}>· {previewData.invalid_rows} invalid</span>
                  )}
                </div>

                {/* Validation Errors */}
                {previewData.validation_errors?.length > 0 && (
                  <div style={{ marginBottom: '20px', border: '1px solid #fca5a5', borderRadius: '8px', overflow: 'hidden' }}>
                    <div style={{ padding: '10px 14px', background: '#fef2f2', borderBottom: '1px solid #fca5a5', fontWeight: '600', fontSize: '13px', color: '#dc2626' }}>
                      Validation Errors ({previewData.validation_errors.length})
                    </div>
                    <div style={{ maxHeight: '140px', overflow: 'auto', padding: '8px 14px' }}>
                      {previewData.validation_errors.map((err, idx) => (
                        <div key={idx} style={{ fontSize: '13px', color: '#991b1b', padding: '6px 0', borderBottom: idx < previewData.validation_errors.length - 1 ? '1px solid #fee2e2' : 'none' }}>
                          <strong>Row {err.row}:</strong> {err.errors.map(e => e.error).join(', ')}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div style={{ overflowX: 'auto', marginBottom: '20px', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                    <thead>
                      <tr style={{ background: '#f8fafc' }}>
                        <th style={{ padding: '10px', textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>Name</th>
                        <th style={{ padding: '10px', textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>Email</th>
                        <th style={{ padding: '10px', textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>Department</th>
                      </tr>
                    </thead>
                    <tbody>
                      {previewData.preview.map((row, i) => (
                        <tr key={i} style={{ borderBottom: '1px solid #e2e8f0', background: (row.has_error || row.phone_error || row.name_error || row.email_error) ? '#fef2f2' : 'transparent' }}>
                          <td style={{ padding: '10px' }}>
                            <span style={{ color: row.name_error ? '#dc2626' : 'inherit', fontWeight: row.name_error ? '600' : 'normal' }}>
                              {row.full_name || <em style={{ color: '#dc2626' }}>[Missing Name]</em>}
                            </span>
                            {row.name_error && (
                              <div style={{ fontSize: '11px', color: '#dc2626', marginTop: '2px' }}>{row.name_error}</div>
                            )}
                          </td>
                          <td style={{ padding: '10px' }}>
                            <span style={{ color: row.email_error ? '#dc2626' : '#64748b', fontWeight: row.email_error ? '600' : 'normal' }}>
                              {row.email || <em style={{ color: '#dc2626' }}>[Missing Email]</em>}
                            </span>
                            {row.email_error && (
                              <div style={{ fontSize: '11px', color: '#dc2626', marginTop: '2px' }}>{row.email_error}</div>
                            )}
                          </td>
                          <td style={{ padding: '10px' }}>
                            {row.department}
                            {row.phone_error && (
                              <div style={{ fontSize: '11px', color: '#dc2626', marginTop: '2px' }}>{row.phone_error}</div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                  <button
                    onClick={() => { setUploadFile(null); setPreviewData(null); }}
                    style={{
                      padding: '10px 18px',
                      background: '#fff',
                      border: '1px solid #cbd5e1',
                      borderRadius: '8px',
                      color: '#475569',
                      fontWeight: '600',
                      fontSize: '0.9rem',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={commitUpload}
                    disabled={(previewData.invalid_rows || 0) > 0}
                    style={{
                      padding: '10px 18px',
                      background: (previewData.invalid_rows || 0) > 0 ? '#94a3b8' : '#3b82f6',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontWeight: '600',
                      fontSize: '0.9rem',
                      cursor: (previewData.invalid_rows || 0) > 0 ? 'not-allowed' : 'pointer',
                      boxShadow: (previewData.invalid_rows || 0) > 0 ? 'none' : '0 4px 12px rgba(59, 130, 246, 0.25)',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    {(previewData.invalid_rows || 0) > 0 ? 'Fix Errors to Import' : 'Confirm & Import'}
                  </button>
                </div>
              </div>
            )}

            {uploadResult && (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <CheckCircle size={48} style={{ color: '#10b981', margin: '0 auto 15px' }} />
                <h3 style={{ margin: '0 0 10px 0' }}>Upload Complete!</h3>
                <p style={{ margin: '0 0 20px 0', color: '#475569' }}>
                  Successfully imported <strong>{uploadResult.created_count}</strong> accounts
                  {uploadResult.updated_count ? <> and updated <strong>{uploadResult.updated_count}</strong> existing accounts</> : null}.
                </p>
                <button
                  onClick={closeUploadModal}
                  style={{
                    padding: '10px 18px',
                    background: '#3b82f6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: '600',
                    fontSize: '0.9rem',
                    cursor: 'pointer',
                    boxShadow: '0 4px 12px rgba(59, 130, 246, 0.25)',
                    transition: 'all 0.2s ease'
                  }}
                >
                  Done
                </button>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Edit Modal */}
      {editModalOpen && editingFaculty && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', padding: '30px', borderRadius: '12px', width: '90%', maxWidth: '500px', boxShadow: '0 10px 25px rgba(0,0,0,0.1)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, fontSize: '1.25rem' }}>Edit Faculty Member</h3>
              <button onClick={() => setEditModalOpen(false)} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#94a3b8' }}>&times;</button>
            </div>
            
            <form onSubmit={handleUpdateFaculty}>
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500', color: '#475569' }}>Full Name</label>
                <input 
                  type="text" 
                  value={editForm.full_name} 
                  onChange={(e) => setEditForm({...editForm, full_name: e.target.value})}
                  style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1' }}
                  required
                />
              </div>
              
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500', color: '#475569' }}>Faculty ID</label>
                <input 
                  type="text" 
                  value={editForm.faculty_id} 
                  onChange={(e) => setEditForm({...editForm, faculty_id: e.target.value})}
                  style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1' }}
                />
              </div>
              
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500', color: '#475569' }}>Department</label>
                <input 
                  type="text" 
                  value={editForm.department} 
                  onChange={(e) => setEditForm({...editForm, department: e.target.value})}
                  style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1' }}
                />
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500', color: '#475569' }}>Designation</label>
                <input 
                  type="text" 
                  value={editForm.designation} 
                  onChange={(e) => setEditForm({...editForm, designation: e.target.value})}
                  style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1' }}
                />
              </div>

              <div style={{ marginBottom: '25px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500', color: '#475569' }}>Phone Number</label>
                <input 
                  type="text" 
                  value={editForm.phone_number} 
                  onChange={(e) => setEditForm({...editForm, phone_number: e.target.value})}
                  style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1' }}
                />
              </div>
              
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button type="button" onClick={() => setEditModalOpen(false)} style={{ padding: '10px 16px', background: '#fff', border: '1px solid #cbd5e1', borderRadius: '8px', cursor: 'pointer' }}>Cancel</button>
                <button type="submit" style={{ padding: '10px 16px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' }}>Save Changes</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmModal 
        {...confirmModal} 
        onCancel={closeConfirmModal} 
      />
    </div>
  );
};

export default FacultyManagement;
